import { lazy, Suspense, type JSX } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { useAuthStore } from './shared/auth/auth.store';
import { AdminShell } from './shared/layouts/AdminShell';
import { PlatformShell } from './shared/layouts/PlatformShell';
import { RouteError } from './shared/layouts/RouteError';
import { Skeleton } from './shared/ui/primitives';
import { t } from './i18n';

/**
 * Routing.
 *
 * Every page is lazy-loaded: a treasurer who never opens
 * the IoT screen should never download it. Two shells, chosen by session kind —
 * a platform user and a society admin see genuinely different products.
 */

// ── Society admin ───────────────────────────────────────────────────────────
const Dashboard = lazy(() => import('./features/dashboard/DashboardPage'));
const Members = lazy(() => import('./features/members/MembersPage'));
const Announcements = lazy(() => import('./features/announcements/AnnouncementsPage'));
const Community = lazy(() => import('./features/community/CommunityPage'));
const Complaints = lazy(() => import('./features/complaints/ComplaintsBoard'));
const ComplaintDetail = lazy(() => import('./features/complaints/ComplaintDetailPage'));
const ComplaintNew = lazy(() => import('./features/complaints/ComplaintNewPage'));
const Visitors = lazy(() => import('./features/visitors/VisitorsPage'));
const Helpers = lazy(() => import('./features/helpers/HelpersPage'));
const Gate = lazy(() => import('./features/gate/GatePage'));
const Amenities = lazy(() => import('./features/amenities/AmenitiesPage'));
const Documents = lazy(() => import('./features/documents/DocumentsPage'));
const Structure = lazy(() => import('./features/structure/StructurePage'));
const Bills = lazy(() => import('./features/bills/BillsPage'));
const Payments = lazy(() => import('./features/payments/PaymentsPage'));
const Accounting = lazy(() => import('./features/accounting/AccountingPage'));
const Subscription = lazy(() => import('./features/subscription/SubscriptionPage'));
const SocietySupport = lazy(() => import('./features/support/SupportPage'));
const Settings = lazy(() => import('./features/settings/SettingsPage'));
const SocietyProfile = lazy(() => import('./features/settings/ProfilePage'));
const Onboarding = lazy(() => import('./features/onboarding/OnboardingWizard'));

// ── Auth ────────────────────────────────────────────────────────────────────
const Login = lazy(() => import('./features/auth/LoginPage'));
const PlatformLogin = lazy(() => import('./features/auth/PlatformLoginPage'));
const OAuthComplete = lazy(() => import('./features/auth/OAuthCompletePage'));
const MagicVerify = lazy(() => import('./features/auth/MagicVerifyPage'));
const SetPassword = lazy(() => import('./features/auth/SetPasswordPage'));
const SelectSociety = lazy(() => import('./features/auth/SelectSocietyPage'));

// ── Platform (Media City) ───────────────────────────────────────────────────
const PlatformDashboard = lazy(() => import('./features/platform/PlatformDashboard'));
const PlatformSocieties = lazy(() => import('./features/platform/SocietiesPage'));
const PlatformPurchases = lazy(() => import('./features/platform/PurchasesPage'));
const PlatformSocietyDetail = lazy(() => import('./features/platform/SocietyDetailPage'));
const PlatformPlans = lazy(() => import('./features/platform/PlansPage'));
const PlatformCoupons = lazy(() => import('./features/platform/CouponsPage'));
const PlatformFlags = lazy(() => import('./features/platform/FlagsPage'));
const PlatformAudit = lazy(() => import('./features/platform/AuditPage'));
const PlatformSupport = lazy(() => import('./features/platform/SupportPage'));
const PlatformHealth = lazy(() => import('./features/platform/HealthPage'));
const PlatformSecurity = lazy(() => import('./features/platform/SecurityPage'));
const PlatformProfile = lazy(() => import('./features/platform/PlatformProfilePage'));

// Platform settings — one lazy chunk per panel, so opening the SEO page does
// not download the backup panel's table.
const SettingsShell = lazy(() => import('./features/platform/settings/SettingsShell'));
const GeneralSettings = lazy(() => import('./features/platform/settings/GeneralSettingsPanel'));
const SeoSettings = lazy(() => import('./features/platform/settings/SeoSettingsPanel'));
const SocialLoginSettings = lazy(() => import('./features/platform/settings/SocialLoginSettingsPanel'));
const MailSettings = lazy(() => import('./features/platform/settings/MailSettingsPanel'));
const FooterSettings = lazy(() => import('./features/platform/settings/FooterSettingsPanel'));
const MaintenanceSettings = lazy(() => import('./features/platform/settings/MaintenanceSettingsPanel'));
const SecuritySettings = lazy(() => import('./features/platform/settings/SecuritySettingsPanel'));
const AnalyticsSettings = lazy(() => import('./features/platform/settings/AnalyticsSettingsPanel'));
const BackupSettings = lazy(() => import('./features/platform/settings/BackupSettingsPanel'));
const IntegrationsSettings = lazy(() => import('./features/platform/settings/IntegrationsSettingsPanel'));
const CataloguesSettings = lazy(() => import('./features/platform/settings/CataloguesSettingsPanel'));
const PlanFeaturesSettings = lazy(() => import('./features/platform/settings/PlanFeaturesSettingsPanel'));
const PaymentsSettings = lazy(() => import('./features/platform/settings/PaymentsSettingsPanel'));

// Marketing content — pages, blog, testimonials, facts.
const Reports = lazy(() => import('./features/reports/ReportsPage'));
const PlatformCompliance = lazy(() => import('./features/platform/CompliancePage'));
const PlatformNotifications = lazy(() => import('./features/platform/NotificationsPage'));
const ContentShell = lazy(() => import('./features/platform/content/ContentShell'));
const ContentPages = lazy(() => import('./features/platform/content/PagesPanel'));
const ContentBlog = lazy(() => import('./features/platform/content/BlogPostsPanel'));
const ContentCategories = lazy(() => import('./features/platform/content/BlogCategoriesPanel'));
const ContentTestimonials = lazy(() => import('./features/platform/content/TestimonialsPanel'));
const ContentFacts = lazy(() => import('./features/platform/content/FactsPanel'));
const ContentSiteCopy = lazy(() => import('./features/platform/content/SiteCopyPanel'));

/** Full-page skeleton while a lazy chunk loads. */
function PageFallback(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true" aria-label={t('Loading')}>
      <Skeleton className="h-8 w-52" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }): JSX.Element {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

/** Redirects an anonymous visitor to the right sign-in screen. */
function RequireSociety(): JSX.Element {
  const { accessToken, kind, activeSocietyId, societies } = useAuthStore();

  if (!accessToken) return <Navigate to="/login" replace />;
  if (kind === 'platform') return <Navigate to="/platform" replace />;

  // Signed in but no society chosen: either pick one, or there is nothing to
  // pick and the account is not yet a member anywhere.
  if (!activeSocietyId) {
    return societies.length > 0 ? <Navigate to="/select-society" replace /> : <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function RequirePlatform(): JSX.Element {
  const { accessToken, kind } = useAuthStore();
  if (!accessToken) return <Navigate to="/platform/login" replace />;
  if (kind !== 'platform') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Sends an already-signed-in visitor away from the login screens. */
function RedirectIfSignedIn(): JSX.Element {
  const { accessToken, kind, activeSocietyId } = useAuthStore();
  if (!accessToken) return <Outlet />;
  if (kind === 'platform') return <Navigate to="/platform" replace />;
  return <Navigate to={activeSocietyId ? '/dashboard' : '/select-society'} replace />;
}

/**
 * The platform sign-in screens. A society session must not block them: the
 * same person often runs a society for testing and owns the platform, and a
 * magic link carries its own credential — it replaces whatever session is
 * open rather than being bounced to that session's dashboard.
 */
function RedirectIfPlatform(): JSX.Element {
  const { accessToken, kind } = useAuthStore();
  if (accessToken && kind === 'platform') return <Navigate to="/platform" replace />;
  return <Outlet />;
}

const routes: RouteObject[] = [
  // ── Public ──────────────────────────────────────────────────────────────
  {
    element: <RedirectIfSignedIn />,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <Lazy><Login /></Lazy> },
    ],
  },
  // Sign-in completions carry their own credential and replace whatever
  // session is open: a website sign-up must land in the *new* society, not
  // bounce to one the browser was already signed in to.
  { path: '/oauth/complete', element: <Lazy><OAuthComplete /></Lazy>, errorElement: <RouteError /> },
  { path: '/set-password', element: <Lazy><SetPassword /></Lazy>, errorElement: <RouteError /> },
  {
    element: <RedirectIfPlatform />,
    errorElement: <RouteError />,
    children: [
      { path: '/platform/login', element: <Lazy><PlatformLogin /></Lazy> },
      { path: '/platform/magic', element: <Lazy><MagicVerify /></Lazy> },
    ],
  },

  // Society selection sits between sign-in and the shell: the token has no
  // society yet, so no tenant-scoped route can load.
  {
    path: '/select-society',
    element: <Lazy><SelectSociety /></Lazy>,
    errorElement: <RouteError />,
  },

  // ── Society admin ───────────────────────────────────────────────────────
  {
    element: <RequireSociety />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AdminShell />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: '/dashboard', element: <Lazy><Dashboard /></Lazy> },
          { path: '/onboarding', element: <Lazy><Onboarding /></Lazy> },
          { path: '/members', element: <Lazy><Members /></Lazy> },
          { path: '/announcements', element: <Lazy><Announcements /></Lazy> },
          { path: '/community', element: <Lazy><Community /></Lazy> },
          { path: '/complaints', element: <Lazy><Complaints /></Lazy> },
          { path: '/complaints/new', element: <Lazy><ComplaintNew /></Lazy> },
          { path: '/complaints/:id', element: <Lazy><ComplaintDetail /></Lazy> },
          { path: '/visitors', element: <Lazy><Visitors /></Lazy> },
          { path: '/helpers', element: <Lazy><Helpers /></Lazy> },
          { path: '/gate', element: <Lazy><Gate /></Lazy> },
          { path: '/amenities', element: <Lazy><Amenities /></Lazy> },
          { path: '/documents', element: <Lazy><Documents /></Lazy> },
          { path: '/structure', element: <Lazy><Structure /></Lazy> },
          { path: '/bills', element: <Lazy><Bills /></Lazy> },
          { path: '/payments', element: <Lazy><Payments /></Lazy> },
          { path: '/reports', element: <Lazy><Reports /></Lazy> },
          { path: '/accounting', element: <Lazy><Accounting /></Lazy> },
          { path: '/subscription', element: <Lazy><Subscription /></Lazy> },
          { path: '/support', element: <Lazy><SocietySupport /></Lazy> },
          { path: '/settings', element: <Lazy><Settings /></Lazy> },
          { path: '/settings/profile', element: <Lazy><SocietyProfile /></Lazy> },
          { path: '/settings/:tab', element: <Lazy><Settings /></Lazy> },
        ],
      },
    ],
  },

  // ── Platform console ────────────────────────────────────────────────────
  {
    element: <RequirePlatform />,
    errorElement: <RouteError />,
    children: [
      {
        element: <PlatformShell />,
        errorElement: <RouteError />,
        children: [
          { path: '/platform', element: <Lazy><PlatformDashboard /></Lazy> },
          { path: '/platform/societies', element: <Lazy><PlatformSocieties /></Lazy> },
          { path: '/platform/purchases', element: <Lazy><PlatformPurchases /></Lazy> },
          { path: '/platform/societies/:id', element: <Lazy><PlatformSocietyDetail /></Lazy> },
          { path: '/platform/plans', element: <Lazy><PlatformPlans /></Lazy> },
          { path: '/platform/coupons', element: <Lazy><PlatformCoupons /></Lazy> },
          { path: '/platform/flags', element: <Lazy><PlatformFlags /></Lazy> },
          { path: '/platform/audit', element: <Lazy><PlatformAudit /></Lazy> },
          { path: '/platform/support', element: <Lazy><PlatformSupport /></Lazy> },
          { path: '/platform/health', element: <Lazy><PlatformHealth /></Lazy> },
          { path: '/platform/security', element: <Lazy><PlatformSecurity /></Lazy> },
          { path: '/platform/profile', element: <Lazy><PlatformProfile /></Lazy> },
          { path: '/platform/compliance', element: <Lazy><PlatformCompliance /></Lazy> },
          { path: '/platform/notifications', element: <Lazy><PlatformNotifications /></Lazy> },
          {
            path: '/platform/content',
            element: <Lazy><ContentShell /></Lazy>,
            children: [
              { index: true, element: <Navigate to="/platform/content/pages" replace /> },
              { path: 'pages', element: <Lazy><ContentPages /></Lazy> },
              { path: 'blog', element: <Lazy><ContentBlog /></Lazy> },
              { path: 'categories', element: <Lazy><ContentCategories /></Lazy> },
              { path: 'testimonials', element: <Lazy><ContentTestimonials /></Lazy> },
              { path: 'facts', element: <Lazy><ContentFacts /></Lazy> },
              { path: 'site-copy', element: <Lazy><ContentSiteCopy /></Lazy> },
            ],
          },
          {
            path: '/platform/settings',
            element: <Lazy><SettingsShell /></Lazy>,
            children: [
              { index: true, element: <Navigate to="/platform/settings/general" replace /> },
              { path: 'general', element: <Lazy><GeneralSettings /></Lazy> },
              { path: 'seo', element: <Lazy><SeoSettings /></Lazy> },
              { path: 'social-login', element: <Lazy><SocialLoginSettings /></Lazy> },
              { path: 'mail', element: <Lazy><MailSettings /></Lazy> },
              { path: 'footer', element: <Lazy><FooterSettings /></Lazy> },
              { path: 'maintenance', element: <Lazy><MaintenanceSettings /></Lazy> },
              { path: 'security', element: <Lazy><SecuritySettings /></Lazy> },
              { path: 'analytics', element: <Lazy><AnalyticsSettings /></Lazy> },
              { path: 'backup', element: <Lazy><BackupSettings /></Lazy> },
              { path: 'integrations', element: <Lazy><IntegrationsSettings /></Lazy> },
              { path: 'catalogues', element: <Lazy><CataloguesSettings /></Lazy> },
              { path: 'plan-features', element: <Lazy><PlanFeaturesSettings /></Lazy> },
              { path: 'payments', element: <Lazy><PaymentsSettings /></Lazy> },
            ],
          },
        ],
      },
    ],
  },

  { path: '*', element: <RouteError notFound /> },
];

export const router = createBrowserRouter(routes);
